export default function CodeTycoonSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - Idle Coding Tycoon
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for Idle Coding
            Tycoon.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I play?
              </h3>
              <p className="mt-1">
                Hire developer tiles to start producing{" "}
                <strong>Lines of Code</strong>. Drag two matching developers
                together to merge them into a single higher-tier developer
                that produces far more. Keep hiring, keep merging, and spend
                your Lines of Code on upgrades to grow faster. It is a relaxed
                idle game — there is no rush and no way to lose.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Do I keep earning while the game is closed?
              </h3>
              <p className="mt-1">
                Yes. Your developers keep generating Lines of Code while the
                game is closed. When you reopen it, you collect the offline
                earnings you accumulated since you last played. Everything is
                calculated on your device using the time that has passed.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does &quot;Ship &amp; Sell&quot; do?
              </h3>
              <p className="mt-1">
                &quot;Ship &amp; Sell&quot; is the prestige reset. It clears
                your current developers and upgrades in exchange for a
                permanent <strong>Funding</strong> multiplier that boosts all
                future earnings. After a Ship &amp; Sell, you start a fresh
                run that grows much faster than before. Prestige when your
                progress slows down to leap ahead in the long run.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does the game work offline?
              </h3>
              <p className="mt-1">
                Yes. The entire game runs on your device and needs no
                internet connection to play. Network access is used only for
                showing ads and the cross-promotion card.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the game show ads, and what is the Pro upgrade?
              </h3>
              <p className="mt-1">
                Ads help support continued development and keep the game free.
                Optional rewarded videos give you temporary in-game boosts if
                you choose to watch them — they are never required. You can
                remove all ads with a one-time <strong>Pro upgrade</strong>{" "}
                from the in-game store. There are no subscriptions.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I restore my Pro purchase?
              </h3>
              <p className="mt-1">
                The Pro upgrade is tied to your Google account. If you
                reinstall the game or set up a new device with the same
                Google account, open the in-game store and tap{" "}
                <strong>Restore Purchases</strong>. As long as you are signed
                in to the same Google account that made the purchase, ads will
                be removed again. Note that the one-time purchase restores, but
                your game progress is stored locally and does not transfer
                (see below).
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                My progress reset, or I switched to a new device. Where did it
                go?
              </h3>
              <p className="mt-1">
                Game progress is stored only on your device — there are no
                accounts and no cloud save. This means progress does not
                transfer between devices, and it is removed if you clear the
                app&apos;s storage or uninstall the game. If your progress
                appears to have reset, it is usually because the app&apos;s
                local data was cleared or the game was reinstalled. We are
                unable to recover local progress remotely because it never
                leaves your device.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The game is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the game. Make sure you have the
                latest version from Google Play and that your device has free
                storage. If the problem continues, email us with your device
                model and what you were doing when it crashed.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details
            in your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your game version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/codetycoon/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/codetycoon/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/codetycoon/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/codetycoon/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> Idle Coding Tycoon
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.codetycoon
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
